// Optional AI features using Google Gemini with the user's own API key
// (https://aistudio.google.com/apikey). Nothing is sent unless an AI feature is used.

import { computeTotals, invoiceStatus, num, projectLine, round2 } from "./utils";

export const AI_MODELS = [
  { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash – fast (recommended)" },
  { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro – most capable" },
  { value: "gemini-2.5-flash-lite", label: "Gemini 2.5 Flash-Lite – cheapest" },
];

export const aiConfig = (settings) => {
  const a = settings?.ai || {};
  return { enabled: !!(a.apiKey && a.enabled !== false), apiKey: a.apiKey || "", model: a.model || AI_MODELS[0].value, importHelp: a.importHelp !== false };
};

// Last connection result, shared by the green/red status light. Kept per device.
const STATUS_KEY = "invoice-studio-ai-status";
const listeners = new Set();
export function aiStatus() {
  try {
    return JSON.parse(localStorage.getItem(STATUS_KEY) || "null");
  } catch {
    return null;
  }
}
function setAiStatus(ok, error = "") {
  const v = { ok, error, at: new Date().toISOString() };
  try {
    localStorage.setItem(STATUS_KEY, JSON.stringify(v));
  } catch {}
  listeners.forEach((f) => f(v));
}
export function onAiStatus(f) {
  listeners.add(f);
  return () => listeners.delete(f);
}

export async function askGemini(settings, opts) {
  try {
    const out = await callGemini(settings, opts);
    setAiStatus(true);
    return out;
  } catch (e) {
    if (aiConfig(settings).enabled && !/valid JSON|blocked|empty answer/.test(e.message)) setAiStatus(false, e.message);
    throw e;
  }
}

async function callGemini(settings, { prompt, system, json = false, temperature = 0.3 }) {
  const { apiKey, model, enabled } = aiConfig(settings);
  if (!enabled) throw new Error("Add your Gemini API key in Settings → AI first.");
  let res;
  try {
    res = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(model)}:generateContent`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-goog-api-key": apiKey },
      body: JSON.stringify({
        ...(system ? { systemInstruction: { parts: [{ text: system }] } } : {}),
        contents: [{ role: "user", parts: [{ text: prompt }] }],
        generationConfig: { temperature, ...(json ? { responseMimeType: "application/json" } : {}) },
      }),
    });
  } catch {
    throw new Error("Couldn’t reach Gemini – check your internet connection.");
  }
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = body?.error?.message || `Gemini error ${res.status}`;
    if (res.status === 400 && /API key/i.test(msg)) throw new Error("Gemini rejected the API key – check it in Settings → AI.");
    if (res.status === 404) throw new Error(`Model “${model}” isn’t available for this key – choose another in Settings → AI.`);
    if (res.status === 429) throw new Error("Gemini usage limit reached – try again in a minute.");
    throw new Error(msg);
  }
  const text = (body?.candidates?.[0]?.content?.parts || []).map((p) => p.text || "").join("").trim();
  if (!text) throw new Error(body?.promptFeedback?.blockReason ? `Gemini blocked the request (${body.promptFeedback.blockReason}).` : "Gemini returned an empty answer.");
  if (!json) return text;
  try {
    return JSON.parse(text.replace(/^```(?:json)?\s*|\s*```$/g, ""));
  } catch {
    throw new Error("Gemini’s answer wasn’t valid JSON.");
  }
}

const ym = (d) => (d || "").slice(0, 7);
const r2 = (n) => round2(n);

// Compact picture of the business for "Ask AI" (no bank details or tax IDs), with
// ready-made monthly and per-client totals so questions like "this month" or
// "who owes me" can be answered without doing arithmetic over hundreds of rows.
export function businessContext(store) {
  const now = new Date();
  const today = now.toISOString().slice(0, 10);
  const thisMonth = today.slice(0, 7);
  const invoices = store.invoices
    .filter((i) => (i.docType || "invoice") === "invoice" && i.status !== "cancelled")
    .map((i) => {
      const t = computeTotals(i);
      return { number: i.number, client: i.billTo?.name || "", date: i.date, month: i.periodMonth || ym(i.date), due: i.dueDate || "", currency: i.currency, total: t.total, paid: r2(t.total - t.balance), balance: t.balance, status: invoiceStatus(i, t), lines: (i.items || []).length };
    })
    .sort((a, b) => (b.date || "").localeCompare(a.date || ""));

  const byMonth = {};
  const byClient = {};
  for (const i of invoices) {
    const m = (byMonth[`${i.month}|${i.currency}`] ||= { month: i.month, currency: i.currency, invoices: 0, billed: 0, received: 0, outstanding: 0 });
    m.invoices++;
    m.billed = r2(m.billed + i.total);
    m.received = r2(m.received + i.paid);
    m.outstanding = r2(m.outstanding + Math.max(0, i.balance));
    const c = (byClient[`${i.client}|${i.currency}`] ||= { client: i.client, currency: i.currency, invoices: 0, billed: 0, outstanding: 0, lastInvoice: "" });
    c.invoices++;
    c.billed = r2(c.billed + i.total);
    c.outstanding = r2(c.outstanding + Math.max(0, i.balance));
    if ((i.date || "") > c.lastInvoice) c.lastInvoice = i.date || "";
  }
  const months = Object.values(byMonth).sort((a, b) => b.month.localeCompare(a.month));

  const clients = store.clients
    .filter((c) => !c.archived)
    .map((c) => {
      const ps = store.projects.filter((p) => p.clientId === c.id && p.status === "active");
      return {
        name: c.name,
        country: c.country || "",
        currency: c.currency || store.settings.defaults.currency,
        activeProjects: ps.length,
        monthlyRecurring: r2(ps.filter((p) => p.billing === "monthly").reduce((s, p) => s + num(p.rate) * (num(p.qty) || 1), 0)),
        projects: ps.slice(0, 60).map((p) => `${projectLine(p)} – ${num(p.rate)}/${p.billing === "monthly" ? "month" : p.billing}${p.startDate ? ` from ${p.startDate}` : ""}`),
      };
    });
  const services = store.projects.filter((p) => !p.clientId && p.status === "active").length;
  const unbilledSales = (store.sales || []).filter((x) => !x.invoiceId).map((x) => ({ date: x.date, client: store.clients.find((c) => c.id === x.clientId)?.name || "", description: x.description, amount: r2(num(x.qty) * num(x.rate)) }));
  const latest = months[0]?.month || "";
  return {
    today,
    thisMonth,
    latestMonthWithInvoices: latest,
    business: store.defaultBrand?.name || "",
    owner: store.settings.ownerName || "",
    counts: { invoices: invoices.length, clients: clients.length, activeProjects: store.projects.filter((p) => p.status === "active" && p.clientId).length, sharedServices: services, unbilledSales: unbilledSales.length },
    monthlyTotals: months.slice(0, 24),
    clientTotals: Object.values(byClient).sort((a, b) => b.billed - a.billed),
    clients,
    invoices: invoices.slice(0, 200),
    unbilledSales: unbilledSales.slice(0, 100),
  };
}

export async function askAboutBusiness(store, question, history = []) {
  const ctx = businessContext(store);
  const convo = history.map((m) => `${m.role === "user" ? "User" : "Assistant"}: ${m.text}`).join("\n");
  return askGemini(store.settings, {
    system: [
      "You are the friendly assistant inside an invoicing app for a small digital agency. Reply in British English, concise and practical: short paragraphs or bullet points, amounts with currency symbols, client names as written.",
      "Use the JSON business data for facts and figures, never invent numbers. monthlyTotals and clientTotals are pre-computed – prefer them over adding up invoices yourself. An invoice's \"month\" is the month it bills for.",
      "If a question is about a period with no invoices (for example this month before invoicing), say so briefly and then answer for latestMonthWithInvoices instead, naming that month.",
      "Greetings and small talk are fine: reply naturally in one line and suggest two or three useful things you can do (e.g. summarise last month, list who owes money, draft a reminder).",
      "When asked to draft an email or message, write it ready to send. General business or invoicing questions that don't need the data can be answered from general knowledge.",
    ].join("\n"),
    prompt: `Business data (JSON):\n${JSON.stringify(ctx)}\n\n${convo ? `Conversation so far:\n${convo}\n\n` : ""}User: ${question}`,
  });
}

export async function writeReminder(store, inv, { tone = "friendly" } = {}) {
  const t = computeTotals(inv);
  const brand = store.brandById(inv.brandId);
  const facts = {
    from: brand?.name,
    client: inv.billTo?.name,
    contact: inv.billTo?.contactPerson || "",
    invoice: inv.number,
    issued: inv.date,
    due: inv.dueDate || "on receipt",
    balance: t.balance,
    currency: inv.currency,
    period: inv.period || "",
    remindersSentBefore: inv.lastReminderAt ? `last reminder ${inv.lastReminderAt}` : "none",
    today: new Date().toISOString().slice(0, 10),
  };
  return askGemini(store.settings, {
    json: true,
    temperature: 0.6,
    system: "You write short, polite, professional payment reminder emails for a small agency. British English. Never invent bank details or facts not given.",
    prompt: `Write a ${tone} payment reminder email. Facts: ${JSON.stringify(facts)}. Return JSON {"subject": string, "body": string}. Mention the invoice number, amount due (with currency) and due date; sign off with the business name.`,
  });
}

// Second opinion for invoices the built-in reader struggled with.
export async function aiReadInvoice(settings, text) {
  const out = await askGemini(settings, {
    json: true,
    temperature: 0,
    system: "You extract data from invoice text precisely. Never guess numbers that are not in the text; use null for anything missing.",
    prompt: `Extract this invoice as JSON with keys: number, date (YYYY-MM-DD), dueDate (YYYY-MM-DD), currency (ISO code), billTo {name, address, email, phone, gstin, country}, items [{description, qty, rate, taxRate}], discount, credit, subtotal, tax, total, status ("paid"|"unpaid"|null). "rate" is the unit price before tax.\n\nINVOICE TEXT:\n${String(text).slice(0, 20000)}`,
  });
  const items = Array.isArray(out.items) ? out.items.filter((l) => l && (l.description || l.rate)) : [];
  return {
    number: out.number || "",
    date: /^\d{4}-\d{2}-\d{2}$/.test(out.date || "") ? out.date : "",
    dueDate: /^\d{4}-\d{2}-\d{2}$/.test(out.dueDate || "") ? out.dueDate : "",
    currency: /^[A-Z]{3}$/.test(out.currency || "") ? out.currency : "",
    billTo: Object.fromEntries(Object.entries(out.billTo || {}).filter(([, v]) => v).map(([k, v]) => [k, String(v)])),
    items: items.map((l) => ({ description: String(l.description || ""), qty: num(l.qty) || 1, rate: num(l.rate), taxRate: num(l.taxRate), hsn: "", tax: 0, amount: round2((num(l.qty) || 1) * num(l.rate)) })),
    discount: num(out.discount),
    credit: num(out.credit),
    total: num(out.total),
    vat: num(out.tax),
    status: out.status === "paid" ? "paid" : out.status === "unpaid" ? "sent" : "",
  };
}
